import { createFileRoute, useParams } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";

import Icon from "@/components/Icon/Icon";
import { HStack, VStack } from "@/components/Layout";
import { ViewportLoader } from "@/components/Loading";
import { Text } from "@/components/Typography/Text";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { useAppIntegrationContract } from "../-query";

import CardFormMasonryGridGroup from "./-components/CardFormMasonryGridGroup";
import TableOfContent from "./-components/TableOfContent";
import { useRulesQuery } from "./-query";

export const Route = createFileRoute(
  "/_staff/app-integrations/$id/_$id/rule-and-policy/",
)({
  component: RuleAndPolicy,
});

const InfoMessage = ({ value }: { value: string }) => (
  <HStack className="gap-1 justify-start px-2 py-0">
    <Icon iconName="InfoIcon" size={16} colorVariant="neutral" />
    <Text variant="label" className="w-full text-primary-muted-on-neutral-fill">
      {value}
    </Text>
  </HStack>
);

function RuleAndPolicy() {
  const { id: appIntegrationId } = useParams({
    from: "/_staff/app-integrations/$id/_$id/rule-and-policy/",
  });
  const { data: appIntegrationData } =
    useAppIntegrationContract(appIntegrationId);
  const { integratedApp } = appIntegrationData;
  const { data, isLoading, isError } = useRulesQuery(appIntegrationId);
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.profile.app.ruleAndPolicy",
  });

  useDocumentTitle(
    t("pageTitle", {
      name: integratedApp.name,
      interpolation: { escapeValue: false },
    }),
  );

  const sectionRefs = useRef<{ [key: string]: React.RefObject<HTMLElement> }>(
    {},
  );
  const handleScrollToSection = useCallback((id: string) => {
    if (sectionRefs.current[id]?.current) {
      sectionRefs.current[id].current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, []);

  const [selectedTab, setSelectedTab] = useState(data?.rules[0]?.roleId ?? "");
  const handleSelectedTab = useCallback(
    (id: string) => {
      setSelectedTab(id);
      handleScrollToSection(id);
    },
    [handleScrollToSection],
  );

  if (isLoading) {
    return <ViewportLoader />;
  }

  if (isError || !data) {
    return;
  }

  return (
    <HStack className="flex gap-9 p-10">
      <VStack className="max-w-[280px] gap-6">
        <TableOfContent
          rules={data.rules}
          selectedTab={selectedTab}
          handleSelectedTab={handleSelectedTab}
        />
        <InfoMessage value={t("common.infoMessage.description")} />
      </VStack>

      <VStack className="gap-5 w-full">
        {data.rules.map((rule) => (
          <CardFormMasonryGridGroup
            key={rule.roleId}
            rule={rule}
            sectionRefs={sectionRefs}
          />
        ))}
      </VStack>
    </HStack>
  );
}
